// src/constants/DISCLAIMERS.ts
// ─────────────────────────────────────────────────────────────────────────
// Single source of truth for every disclaimer string in the app.
// If you change wording materially, bump TOS_VERSION so existing users
// are re-prompted to accept on next session.
//
// V1 legal copy approved against Terms / Disclaimer / Privacy draft set.
// All user-facing acceptance and disclaimer language must be sourced from
// here — never hardcoded in components — so it cannot drift out of sync.
// ─────────────────────────────────────────────────────────────────────────

export const TOS_VERSION = '1.1.0';
export const MIN_AGE = 18;

// ── Per-document acceptance versions ────────────────────────────────────────
// The server records acceptance per doc type (tos / privacy / disclaimer / age)
// with an independent version string each. Today all three legal docs move
// together under TOS_VERSION, but modeling them separately means Privacy can be
// re-versioned without forcing ToS re-acceptance later. Bumping any of these
// re-prompts the user for THAT document on their next session. AGE_VERSION is
// tied to the minimum age: it only changes if the age gate itself changes.
export const PRIVACY_VERSION = TOS_VERSION;
export const DISCLAIMER_VERSION = TOS_VERSION;
export const AGE_VERSION = `${MIN_AGE}+`;

/**
 * The four acceptance facts recorded on signup / re-acceptance, each with the
 * version the user is agreeing to. The server stores one row per entry. The UI
 * still presents this as two checkboxes (age + the combined legal line) — the
 * fan-out to four records happens at the API boundary, not in the user's view.
 */
export const ACCEPTANCE_DOCS = [
	{ docType: 'tos', version: TOS_VERSION },
	{ docType: 'privacy', version: PRIVACY_VERSION },
	{ docType: 'disclaimer', version: DISCLAIMER_VERSION },
	{ docType: 'age', version: AGE_VERSION },
] as const;

/**
 * The legal moment-of-acceptance language. This is the most legally
 * load-bearing copy in the product. `age` and `terms` render as separate
 * checkboxes; "Terms of Service", "Privacy Policy", and "Disclaimer" within
 * `terms` are rendered as links by the consuming component.
 */
export const SIGNUP_ACCEPTANCE = {
	age: `I am ${MIN_AGE} years of age or older.`,
	terms:
		'I have read and agree to the Terms of Service, Privacy Policy, and Disclaimer. ' +
		'I understand Nisk is for educational purposes only; does not provide ' +
		'personalized financial, investment, tax, legal, accounting, credit, insurance, ' +
		'or professional advice; does not recommend what I should buy, sell, borrow, ' +
		'invest in, or do with my money; and uses simplified projections that are not ' +
		'predictions.',
} as const;

/**
 * Shown in the blocking re-acceptance modal when TOS_VERSION changes.
 * Same legal substance as signup, phrased for the "updated terms" context.
 */
export const REACCEPTANCE = {
	age: `I am ${MIN_AGE} years of age or older.`,
	terms:
		'I have read and agree to the updated Terms of Service, Privacy Policy, and ' +
		'Disclaimer. I understand Nisk is educational only and does not provide ' +
		'personalized financial, investment, tax, legal, accounting, credit, insurance, ' +
		'or professional advice.',
} as const;

/**
 * Persistent line in the global footer. Always visible, never blocks.
 */
export const FOOTER_NOTICE =
	'Educational only. Not financial, investment, tax, legal, accounting, ' +
	'credit, or insurance advice. Projections are illustrative, not predictions.';

/**
 * Variants for the reusable DisclaimerBlock component.
 */
export const DISCLAIMER_VARIANTS = {
	education:
		'This content is for educational purposes only. It is not personalized ' +
		'financial, investment, tax, legal, accounting, credit, insurance, or ' +
		'professional advice.',

	projection:
		'Based on numbers you entered and simplified assumptions. Educational ' +
		'estimate only. Not a prediction, recommendation, or financial plan. ' +
		'Actual results will differ, often substantially, and past performance does ' +
		'not guarantee future results.',

	dashboard:
		'Based on numbers you entered and simplified assumptions. Educational ' +
		'estimate only. Not a prediction, recommendation, or financial plan.',

	speculative:
		'This content discusses speculative strategies that can lead to rapid or ' +
		'total loss. Learn the ideas and risks; do not treat this as a roadmap or ' +
		'recommendation.',

	tax:
		'This is general education. Tax, debt, and credit decisions depend on your ' +
		'specific situation and local rules. Consult a qualified professional before ' +
		'acting.',

	debt:
		'This is general education. Debt and credit decisions depend on your specific ' +
		'situation, including interest rates, balances, cash flow, and local rules. ' +
		'Consult a qualified professional before acting.',

	history:
		'This replay uses real historical market data (monthly averages) for ' +
		'education. Past performance does not predict or guarantee future ' +
		'results, and nothing here is a recommendation to buy, sell, or hold ' +
		'anything.',
} as const;

export type DisclaimerVariant = keyof typeof DISCLAIMER_VARIANTS;

/**
 * First-time welcome modal copy.
 */
export const WELCOME_PROMISE = {
	title: 'Before you start.',
	body: [
		'Nisk teaches money concepts. We do not sell investments, manage ' +
			'accounts, move money, place trades, or tell you what to buy.',
		'Every projection, calculator, dashboard, and scenario in this app is ' +
			'illustrative \u2014 built from numbers you enter. Real markets, real taxes, ' +
			'and real life will look different.',
		'Nisk does not know your full financial life. Treat outputs as learning ' +
			'tools, not instructions. When you make actual financial decisions, talk to a ' +
			'qualified professional who knows your situation.',
	],
	cta: "I understand \u2014 let's learn.",
} as const;
